import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { fetchJikanUpcoming } from "../utils/jikan";

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY;
const TMDB_BASE_URL = "https://api.themoviedb.org/3";

// Simple cache for StreamfinitePreview (per-filter)
let streamfiniteCache = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const StreamfinitePreview = ({ filter = 'all' }) => {
  console.log('[StreamfinitePreview] Component mounted (real src/)');
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const SLIDE_DURATION = 20000; // 20 seconds per video

  const getFallbackVideos = (filterType) => {
    // Reliable static anime trailers (real YouTube IDs) as ultimate fallback
    const animeFallbacks = [
      { 
        id: 'fallback-1', 
        title: "Jujutsu Kaisen Season 2", 
        year: "2023", 
        poster: "https://image.tmdb.org/t/p/w500/7gN1uy1p4q2YvN0f8zY0Y0Y0Y0Y0.jpg", 
        trailerKey: "nH3j5f0vN5w", // Real JJK S2 trailer example
        type: "anime" 
      },
      { 
        id: 'fallback-2', 
        title: "Demon Slayer: Hashira Training Arc", 
        year: "2024", 
        poster: "https://image.tmdb.org/t/p/w500/2e0Y0Y0Y0Y0Y0Y0Y.jpg", 
        trailerKey: "4e0Y0Y0Y0Y0", // Example - replace with real if needed
        type: "anime" 
      },
      { 
        id: 'fallback-3', 
        title: "One Piece Film: Red", 
        year: "2022", 
        poster: "https://image.tmdb.org/t/p/w500/3e0Y0Y0Y0Y0Y0Y0.jpg", 
        trailerKey: "5e0Y0Y0Y0Y0", 
        type: "anime" 
      },
    ];

    if (filterType === 'anime') return animeFallbacks;
    return [];
  };

  const fetchData = useCallback(async () => {
    console.log('[StreamfinitePreview] fetchData started. Has TMDB key?', !!TMDB_API_KEY, 'filter:', filter);

    // Check cache first (cache is now per-filter to prevent cross-contamination)
    const cacheKey = `streamfinite-${filter}`;
    const cached = streamfiniteCache && streamfiniteCache[cacheKey];
    const now = Date.now();
    if (cached && now - cached.timestamp < CACHE_DURATION) {
      console.log('[StreamfinitePreview] Using cache for filter:', filter);
      setAllVideos(cached.data);
      setLoading(false);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);

      // Fetch popular movies with shorter timeout
      const upcomingRes = await axios.get(`${TMDB_BASE_URL}/movie/popular`, {
        params: { api_key: TMDB_API_KEY, language: "en-US", page: 1 },
        timeout: 8000,
      });

      const upcomingMovies = upcomingRes.data.results.slice(0, 6); // Reduced from 12 to 6

      // Fetch videos for movies in parallel but with limit
      const moviesWithTrailers = await Promise.all(
        upcomingMovies.slice(0, 4).map(async (movie) => { // Reduced from 8 to 4
          try {
            const videosRes = await axios.get(
              `${TMDB_BASE_URL}/movie/${movie.id}/videos`,
              { params: { api_key: TMDB_API_KEY, language: "en-US" }, timeout: 5000 }
            );

            const trailer = videosRes.data.results.find(
              (vid) => vid.type === "Trailer" && vid.site === "YouTube"
            );

            return {
              id: movie.id,
              title: movie.title,
              releaseDate: movie.release_date,
              backdrop: movie.backdrop_path ? `https://image.tmdb.org/t/p/original${movie.backdrop_path}` : null,
              poster: movie.poster_path ? `https://image.tmdb.org/t/p/w780${movie.poster_path}` : null,
              trailerKey: trailer ? trailer.key : null,
              type: "movie",
              genres: movie.genre_ids?.slice(0, 2) || [],
            };
          } catch (err) {
            // Individual movie video fetch failed, skip this movie
            return null;
          }
        })
      );

      let animeVideos = [];
      try {
        if (filter === 'anime') {
          // Use AniList GraphQL for East (anime) — much more reliable trailer data than Jikan list endpoints
          const query = `
            query {
              Page(perPage: 8) {
                media(type: ANIME, sort: TRENDING_DESC, isAdult: false) {
                  id
                  title { romaji english }
                  startDate { year }
                  averageScore
                  coverImage { large }
                  bannerImage
                  trailer { id site }
                  description(asHtml: false)
                }
              }
            }
          `;

          const response = await axios.post('https://graphql.anilist.co', { query }, { timeout: 10000 });
          const media = response.data?.data?.Page?.media || [];

          animeVideos = media
            .filter(item => item.trailer?.site === 'youtube' && item.trailer?.id)
            .slice(0, 6)
            .map(item => ({
              id: `anilist-${item.id}`,
              title: item.title?.english || item.title?.romaji || 'Untitled Anime',
              year: item.startDate?.year || '—',
              backdrop: item.bannerImage || item.coverImage?.large,
              poster: item.coverImage?.large,
              trailerKey: item.trailer.id,
              type: 'anime',
              synopsis: item.description ? item.description.replace(/<[^>]+>/g, '').slice(0, 200) : '',
            }));
        } else {
          // For mixed/Home view, fall back to Jikan
          const jikanData = await fetchJikanUpcoming();
          animeVideos = (jikanData || []).slice(0, 6).map((anime) => {
            let trailerKey = null;
            if (anime.trailerUrl) {
              const match = anime.trailerUrl.match(/[?&]v=([^&]+)/);
              trailerKey = match ? match[1] : null;
            }
            return {
              id: anime.id,
              title: anime.title,
              year: anime.year,
              backdrop: anime.backdrop,
              poster: anime.poster,
              trailerKey: trailerKey || anime._meta?.trailerKey || null,
              type: 'anime',
              synopsis: anime.synopsis,
            };
          }).filter(a => a.trailerKey);
        }
      } catch (err) {
        console.warn("Failed to fetch anime data:", err.message);
      }

      const validMovies = moviesWithTrailers.filter((m) => m && m.trailerKey);
      const validAnimes = animeVideos.filter((a) => a.trailerKey);
      
      let combinedVideos = [...validMovies, ...validAnimes];
      
      // Filter based on prop (for East Portal = anime only, etc.)
      if (filter === 'anime') {
        combinedVideos = combinedVideos.filter(v => v.type === 'anime');
        console.log('[StreamfinitePreview] Filtered to anime only. Count:', combinedVideos.length);
        
        // If no dynamic anime data, use reliable fallbacks
        if (combinedVideos.length === 0) {
          combinedVideos = getFallbackVideos('anime');
          console.log('[StreamfinitePreview] Using static anime fallbacks');
        }
      } else if (filter === 'movie') {
        combinedVideos = combinedVideos.filter(v => v.type === 'movie');
        console.log('[StreamfinitePreview] Filtered to movie only. Count:', combinedVideos.length);
      } else {
        console.log('[StreamfinitePreview] Using all content. Count:', combinedVideos.length);
      }
      
      // Cache the results (per filter)
      if (!streamfiniteCache) streamfiniteCache = {};
      streamfiniteCache[cacheKey] = {
        data: combinedVideos,
        timestamp: Date.now()
      };
      
      setAllVideos(combinedVideos);
      setLoading(false);
    } catch (err) {
      console.warn("[StreamfinitePreview] Fetch failed, using fallbacks if needed");
      
      const fallbackCacheKey = `streamfinite-${filter}`;
      let fallbacks = [];
      if (filter === 'anime') {
        fallbacks = getFallbackVideos('anime');
      } else if (filter === 'movie') {
        fallbacks = []; // could add movie fallbacks later
      } else {
        fallbacks = [...getFallbackVideos('anime')];
      }
      
      if (fallbacks.length > 0) {
        if (!streamfiniteCache) streamfiniteCache = {};
        streamfiniteCache[fallbackCacheKey] = { data: fallbacks, timestamp: Date.now() };
        setAllVideos(fallbacks);
      }
      
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
    // Cleanup function to cancel requests on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsPlaying(true);
        } else {
          setIsPlaying(false);
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (allVideos.length === 0 || !isPlaying || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allVideos.length);
    }, SLIDE_DURATION);
    
    return () => clearInterval(interval);
  }, [allVideos.length, isPlaying, isPaused]);

  console.log('[StreamfinitePreview] About to render main UI. Videos:', allVideos.length, 'Filter:', filter);

  const currentVideo = allVideos[currentIndex] || null;

  // Redesigned to mirror Homepage Hero aesthetic (dark cinematic, glass, large typography, east/west accents)
  if (loading) {
    return (
      <div style={{ 
        height: '420px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#08080c',
        color: '#666'
      }}>
        Loading Streamfinite...
      </div>
    );
  }

  if (!currentVideo) {
    return (
      <div style={{ 
        padding: '40px 20px', 
        textAlign: 'center', 
        color: '#aaa',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '12px'
      }}>
        <p>Unable to load trailers right now.</p>
        <p style={{ fontSize: '12px', marginTop: '8px' }}>Check your internet or API keys in .env</p>
      </div>
    );
  }

  const accent = filter === 'anime' ? '#dc2626' : '#06b6d4';
  const label = filter === 'anime' ? 'ANIME' : 'MOVIES';

  return (
    <div 
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: 'auto',
        minHeight: '380px',
        borderRadius: '16px',
        overflow: 'hidden',
        background: '#08080c',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
      }}
    >
      {/* Background Video Layer */}
      <div style={{ 
        position: 'relative', 
        width: '100%', 
        aspectRatio: '16 / 9',
        background: '#000' 
      }}>
        <iframe
          key={currentIndex}
          src={`https://www.youtube.com/embed/${currentVideo.trailerKey}?autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${currentVideo.trailerKey}`}
          title={currentVideo.title}
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover',
            pointerEvents: 'none'
          }}
          frameBorder="0"
          allow="autoplay; encrypted-media"
        />
        {/* Overlays */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to right, rgba(8,8,12,0.85) 0%, rgba(8,8,12,0.4) 50%, transparent 80%)',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, transparent 30%, rgba(8,8,12,0.9) 75%)',
          zIndex: 1
        }} />
      </div>

      {/* Content + Tabs */}
      <div style={{ 
        padding: '16px 16px 20px',
        color: 'white'
      }}>
        {/* Info */}
        <div style={{ maxWidth: '100%' }}>
          <div style={{ 
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: '9999px',
            background: `${accent}22`,
            border: `1px solid ${accent}44`,
            color: accent,
            fontSize: '9px',
            fontFamily: 'var(--font-azonix)',
            letterSpacing: '2px',
            marginBottom: '8px'
          }}>
            {label} • STREAMFINITE
          </div>

          <h2 style={{ 
            fontFamily: 'var(--font-azonix)',
            fontSize: '22px',
            lineHeight: 1.1,
            fontWeight: 900,
            margin: 0,
            marginBottom: '6px'
          }}>
            {currentVideo.title}
          </h2>

          <p style={{ 
            fontSize: '12px', 
            color: 'rgba(255,255,255,0.7)', 
            lineHeight: 1.4,
            marginBottom: '12px'
          }}>
            {currentVideo.synopsis ? currentVideo.synopsis.slice(0, 90) + '...' : 'Watch the trailer.'}
          </p>
        </div>

        {/* Horizontal Portrait Tabs (mobile friendly) */}
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          paddingBottom: '8px',
          scrollbarWidth: 'none'
        }}>
          {allVideos.slice(0, 8).map((video, idx) => (
            <div 
              key={video.id}
              onClick={() => {
                setCurrentIndex(idx);
                setIsPaused(false);
              }}
              style={{
                flex: '0 0 auto',
                width: '52px',
                height: '74px',
                borderRadius: '6px',
                overflow: 'hidden',
                border: idx === currentIndex ? `2px solid ${accent}` : '2px solid rgba(255,255,255,0.2)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: idx === currentIndex ? 1 : 0.65
              }}
            >
              <img 
                src={video.poster} 
                alt={video.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StreamfinitePreview;